import { Button, IconButton, Menu, TooltipShortcut } from "@itk/mui-components";
import { hasValue } from "@itk/typescript-utils";
import { Grid, MenuItem, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useFileDownload } from "src/hooks";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { getFileNameWithoutExtension } from "./document-viewer-utils";
import { DekoxIcon } from "../deko-x-icons";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";

const mapStateToProps = (state: RootState) => ({
  ...state.generalReducer.applicationReducer
});

type Props = ReturnType<typeof mapStateToProps> & {
  fileId: string;
};

const DocumentViewerBase = (props: Props) => {
  const { fileId } = props;
  const [downloadFileId, setDownloadFileId] = useState<string>();
  const { fileInfo: fileInfoPreview } = useFileDownload(fileId, true);
  const { fileInfo } = useFileDownload(downloadFileId);
  const ref = useRef<HTMLAnchorElement>(null);
  const refDownloadPdf = useRef<HTMLAnchorElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const genericTranslationsT = useGenericTranslationsT();

  useEffect(() => {
    if (hasValue(ref.current) && hasValue(fileInfo)) {
      ref.current.click();
      setDownloadFileId(undefined);
    }
  }, [fileInfo, ref.current]);

  const handlePrint = useCallback(() => {
    if (hasValue(iframeRef)) {
      iframeRef.current?.contentWindow?.print();
    }
  }, [iframeRef]);

  const downloadPdfFile = useCallback(() => {
    if (hasValue(fileInfoPreview) && hasValue(refDownloadPdf.current)) {
      refDownloadPdf.current.click();
    }
  }, [fileInfoPreview, refDownloadPdf.current]);

  return (
    <Stack height="100%">
      {hasValue(fileInfoPreview) && (
        <>
          <Grid container>
            <Grid size={9}>
              <Typography variant="h5" flex="1">
                {getFileNameWithoutExtension(fileInfoPreview.fileName)}
              </Typography>
            </Grid>
            <Grid size={3} sx={{ display: "flex" }} justifyContent="flex-end">
              <IconButton k={genericTranslationsK.dynamicValue} title="" onClick={ev => setAnchorElement(ev.currentTarget)}>
                <DekoxIcon icon="moreVertical" />
              </IconButton>
              <Menu
                open={hasValue(anchorElement)}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                onClose={() => setAnchorElement(null)}
                anchorEl={anchorElement}
              >
                <MenuItem onClick={handlePrint}>
                  <DekoxIcon icon="print" />
                  <Typography ml={1}>{genericTranslationsT.print}</Typography>
                </MenuItem>
                <MenuItem onClick={downloadPdfFile}>
                  <DekoxIcon icon="download" />
                  <Typography ml={1}>{genericTranslationsT.download}</Typography>
                </MenuItem>
                {hasValue(fileInfoPreview) && fileInfoPreview.originalExtension.toLowerCase() !== "pdf" && (
                  <MenuItem>
                    <TooltipShortcut
                      shortcut={{
                        action: () => setDownloadFileId(fileId),
                        shortCutKey: "g",
                        modifier: "alt"
                      }}
                    >
                      <Button
                        t={genericTranslationsT}
                        field="dynamicValue"
                        k={genericTranslationsK}
                        tArgs={[`Download ${fileInfoPreview.originalExtension.toLowerCase()}`]}
                        onClick={() => {
                          setDownloadFileId(fileId);
                        }}
                        startIcon={<DekoxIcon icon="download" />}
                      />
                    </TooltipShortcut>
                  </MenuItem>
                )}
              </Menu>
            </Grid>
          </Grid>
          <iframe ref={iframeRef} width="100%" height="100%" src={fileInfoPreview.url + "#zoom=100&scrollbar=0&toolbar=0&navpanes=0"} id="pdf_content" />
          <div style={{ display: "none" }}>
            <a
              ref={ref}
              download={fileInfo?.fileName}
              href={fileInfo?.url}
              onContextMenu={e => {
                e.preventDefault();
                return false;
              }}
            />
            <a
              ref={refDownloadPdf}
              download={fileInfoPreview?.fileName}
              href={fileInfoPreview?.url}
              onContextMenu={e => {
                e.preventDefault();
                return false;
              }}
            />
          </div>
        </>
      )}
    </Stack>
  );
};

export const DocumentViewer = connect(mapStateToProps, undefined)(DocumentViewerBase);
